import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ErrorBoundary } from "@/components/common";
import { ToastProvider } from "@/components/ui/ToastProvider";
import { LoginPage, VerifyPage, DashboardPage, NotFoundPage } from "@/pages";
import { JumppageLogin } from "@/pages/JumppageLogin";
import { EmailTemplatePreviewPage } from "@/pages/EmailTemplatePreviewPage";
import { SuperadminEmailOutboxPage } from "@/pages/SuperadminEmailOutboxPage";
import { EngineerAcceptPage } from "@/pages/EngineerAcceptPage";
import { EngineerSchoolSelectPage } from "@/pages/EngineerSchoolSelectPage";
import { PrepRequestLandingPage } from "@/pages/PrepRequestLandingPage";
import { SubstitutionRequestLandingPage } from "@/pages/SubstitutionRequestLandingPage";
import { AdminVerifyPage } from "@/pages/AdminVerifyPage";
import { CollectionPhasePage } from "@/pages/CollectionPhasePage";
import { CommitPhasePage } from "@/pages/CommitPhasePage";
import { EmailActionPage } from "@/pages/EmailActionPage";

import { ConfigPage } from "@/pages/ConfigPage";
import { UnicodeTestPage } from "@/pages/UnicodeTestPage";
import { MicCheckPage } from "@/pages/MicCheckPage";
import {
  TeacherDashboardPage,
  TeacherTimetablePage,
  TeacherRequestsPage,
  TeacherRequestPage,
  TeacherRequestPrepPage,
  TeacherIncomingRequestsPage,
  TeacherBookingsPage,
  TeacherChatsPage,
  MessagesPage,
  TeacherSettingsPage,
  HRReportPage,
  SubstitutionManagementPage,
  WaitingPoolAdminPage,
  AdminManagementPage,
  TeacherParamsPage,
  SuperadminManagementPage,
  SchoolManagementPage,
  QualificationMatrixPage,
  TimetableManagementPage,
  HelpPage,
  FileManagementPage,
  TeacherCodeManagementPage,
  SubstitutionsLogPage,
  LiveStatusPage,
  TimetableImportPage,
  AdminTimetableLoadPage,
  TimetableDataQualityPage,
  UltraadminDashboardPage,
  UltraadminActAsTeacherPage,
  SuperuserManagementPage,
  UltraadminStatisticsPage,
  UltraadminSettingsPage,
  SuperadminDashboardPage,
  EngineerManagementPage,
  UltraadminPatternPipelinePage,
  UltraadminGlobalPatternsPage,
  SuperadminPatternEditorPage,
  AdminSettingsPage,
  PartnerSchoolsPage,
  PartnerSuperadminsPage,
  PartnerTeamPage,
  PartnerBillingPage,
  UltraadminPartnersPage,
  SchoolBillingPage,
  UltraadminListPricesPage,
  UltraadminSchoolTermsPage,
  UltraadminInvoicesPage,
  UltraadminBillingTicketsPage,
  UltraadminOverduePage,
  PublicPayPage,
  TimetableExportPage,
  TimetableExportOpenPage,
} from "@/pages";
import { DirectorDashboardPage } from "@/pages/DirectorDashboardPage";
import { DirectorSuperadminPage } from "@/pages/DirectorSuperadminPage";
import { DirectorCasesPage } from "@/pages/DirectorCasesPage";
import { EffectiveStundenplanPage } from "@/pages/EffectiveStundenplanPage";
import { DirectorDelegationPage } from "@/pages/DirectorDelegationPage";
import { DirectorSettingsPage } from "@/pages/DirectorSettingsPage";
import { HRDirectorDecisionsPage } from "@/pages/HRDirectorDecisionsPage";
import { HRLehrerpultPage } from "@/pages/HRLehrerpultPage";
import { HRAbrechnungstabellePage } from "@/pages/HRAbrechnungstabellePage";
import { HRIpbPage } from "@/pages/HRIpbPage";
import { HRSettingsPage } from "@/pages/HRSettingsPage";
import { HRHomePage } from "@/pages/HRHomePage";
import { HrWorkRedirect } from "@/components/hr/HrWorkRedirect";
import { AdminTeacherListPage } from "@/pages/AdminTeacherListPage";
import { AdminOnBehalfRequestPage } from "@/pages/AdminOnBehalfRequestPage";
import { SneakPeekPage } from "@/pages/SneakPeekPage";
import { AdminStellvertretungPage } from "@/pages/AdminStellvertretungPage";
import { RetroDocsLandingPage } from "@/pages/RetroDocsLandingPage";
import { TeacherLinkingPage } from "@/pages/TeacherLinkingPage";
import { CodeMatchingReportPage } from "@/pages/CodeMatchingReportPage";
import { SuperadminAuditLogPage } from "@/pages/SuperadminAuditLogPage";
import { AutoReRequestPage } from "@/pages/AutoReRequestPage";
import { CancellationManagementPage } from "@/pages/CancellationManagementPage";
import { ConflictManagementPage } from "@/pages/ConflictManagementPage";
import { AppLayout } from "@/components/layout/AppLayout";
import { MaintenanceBanner } from "@/components/layout/MaintenanceBanner";
import { WebAuthnEnrollModal } from "@/components/auth/WebAuthnEnrollModal";
import { TalkGate } from "@/talk/TalkGate";
import { useAuthStore, waitForAuthHydration } from "@/store";
import type { Role } from "@/types";
import { useEffect, useState } from "react";
import {
  isSafeInternalPath,
  shouldBootstrapStoredSession,
} from "@/utils/authRedirect";
import { roleMeetsMinRole } from "@/utils/roleAccess";
import { schoolBillingVisible } from "@/utils/schoolBilling";
import {
  getAccessToken,
  getRefreshToken,
  hydrateTokensFromCookies,
  restoreDurableTokens,
} from "@/utils/tokenStorage";
import { isStandaloneDisplay } from "@/utils/pwaDisplay";
import { restorePasskeyFlag } from "@/utils/deviceUnlock";

/* Note: The following old pages are kept for backward compat during migration:
 - HolidayManagementPage, ReminderConfigPage, WaveConfigPage, CalendarConfigPage
   are NO LONGER imported. Their routes now point to consolidated pages. */

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, refetchOnWindowFocus: false },
  },
});

function AuthSpinner() {
  return (
    <div className="flex items-center justify-center min-h-screen">
      <p className="text-gray-500">Laden...</p>
    </div>
  );
}

function useAuthGate() {
  const { isAuthenticated, isLoading, user } = useAuthStore((state) => ({
    isAuthenticated: state.isAuthenticated,
    isLoading: state.isLoading,
    user: state.user,
  }));
  const [hydrated, setHydrated] = useState(() =>
    useAuthStore.persist.hasHydrated()
  );
  useEffect(() => {
    if (hydrated) return;
    return useAuthStore.persist.onFinishHydration(() => setHydrated(true));
  }, [hydrated]);
  const hasSession = Boolean(getAccessToken() || getRefreshToken());
  const blocking = !hydrated || isLoading || (hasSession && !isAuthenticated);
  return { blocking, isAuthenticated, user };
}

function ProtectedRoute({
  children,
  minRole,
}: {
  children: React.ReactNode;
  minRole?: Role;
}) {
  const location = useLocation();
  const { blocking, isAuthenticated, user } = useAuthGate();

  if (blocking) {
    return <AuthSpinner />;
  }

  if (!isAuthenticated || !user) {
    const next = `${location.pathname}${location.search}`;
    const to = isSafeInternalPath(next)
      ? `/login?next=${encodeURIComponent(next)}`
      : "/login";
    return <Navigate to={to} replace />;
  }

  if (
    minRole === "superadmin" &&
    location.pathname.startsWith("/superadmin/engineers") &&
    user.role !== "superadmin"
  ) {
    return (
      <Navigate
        to={user.role === "engineer" ? "/messages" : "/dashboard"}
        replace
      />
    );
  }
  const acting = Boolean(
    user.acting_role ||
    user.acting_teacher_id ||
    user.acting_user_id ||
    user.acting_partner_company_id
  );
  const effectiveRole = (user.acting_role || user.role) as typeof user.role;
  if (
    location.pathname.startsWith("/ultraadmin/act-as") &&
    (acting || user.role === "ultraadmin" || effectiveRole === "ultraadmin")
  ) {
    return <>{children}</>;
  }
  if (
    effectiveRole === "ultraadmin" &&
    !acting &&
    location.pathname.startsWith("/superadmin/dashboard")
  ) {
    return <Navigate to="/ultraadmin/dashboard" replace />;
  }
  if (user.role === "ultraadmin" && minRole === "teacher") {
    if (!acting) {
      return <Navigate to="/ultraadmin/act-as" replace />;
    }
    return <>{children}</>;
  }
  if (
    acting &&
    location.pathname.startsWith("/ultraadmin/") &&
    !location.pathname.startsWith("/ultraadmin/act-as") &&
    effectiveRole !== "ultraadmin"
  ) {
    return <Navigate to="/dashboard" replace />;
  }
  if (
    !roleMeetsMinRole(
      effectiveRole,
      minRole,
      location.pathname,
      user.engineer_scopes
    )
  ) {
    return (
      <Navigate
        to={effectiveRole === "engineer" ? "/messages" : "/dashboard"}
        replace
      />
    );
  }

  return <>{children}</>;
}

function BillingRoute({ children }: { children: React.ReactNode }) {
  const { blocking, isAuthenticated, user } = useAuthGate();
  if (blocking) {
    return <AuthSpinner />;
  }
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }
  if (!schoolBillingVisible(user)) {
    return (
      <Navigate
        to={user.role === "engineer" ? "/messages" : "/dashboard"}
        replace
      />
    );
  }
  return <>{children}</>;
}

function ActiveAdminRoute({ children }: { children: React.ReactNode }) {
  const { blocking, isAuthenticated, user } = useAuthGate();
  if (blocking) {
    return <AuthSpinner />;
  }
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }
  const role = user.acting_role || user.role;
  if (role !== "admin") {
    return (
      <Navigate
        to={user.role === "engineer" ? "/messages" : "/dashboard"}
        replace
      />
    );
  }
  return <>{children}</>;
}

function ExactAdminRoute({
  children,
  scope = "substitution.request.on_behalf",
}: {
  children: React.ReactNode;
  scope?: string;
}) {
  const { blocking, isAuthenticated, user } = useAuthGate();
  if (blocking) {
    return <AuthSpinner />;
  }
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }
  const engineerOk =
    user.role === "engineer" && (user.engineer_scopes ?? []).includes(scope);
  if (user.role !== "admin" && !engineerOk) {
    return (
      <Navigate
        to={user.role === "engineer" ? "/messages" : "/dashboard"}
        replace
      />
    );
  }
  return <>{children}</>;
}

function App() {
  const fetchUser = useAuthStore((state) => state.fetchUser);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      await waitForAuthHydration();
      if (cancelled) return;
      await restoreDurableTokens();
      if (cancelled) return;
      await restorePasskeyFlag();
      if (cancelled) return;
      hydrateTokensFromCookies();
      if (isStandaloneDisplay()) {
        void navigator.storage?.persist?.().catch(() => {});
      }
      if (!shouldBootstrapStoredSession(window.location.pathname)) return;
      if (
        getAccessToken() ||
        getRefreshToken() ||
        useAuthStore.getState().isAuthenticated
      ) {
        await fetchUser();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchUser]);

  useEffect(() => {
    const disableAutoCorrect = (el: HTMLElement) => {
      if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
        const ac = el.getAttribute("autocomplete") || "";
        if (
          ac === "one-time-code" ||
          ac === "email" ||
          el.dataset.allowAutocomplete === "1"
        ) {
          return;
        }
        el.setAttribute("autocomplete", "off");
        el.setAttribute("autocorrect", "off");
        el.setAttribute("autocapitalize", "off");
        el.setAttribute("spellcheck", "false");
      }
    };
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node instanceof HTMLElement) {
            disableAutoCorrect(node);
            node
              .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
                "input, textarea"
              )
              .forEach((el) => disableAutoCorrect(el));
          }
        }
      }
    });
    document
      .querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
        "input, textarea"
      )
      .forEach((el) => disableAutoCorrect(el));
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <div className="flex flex-col h-screen overflow-hidden">
            <MaintenanceBanner />
            <div className="flex-1 min-h-0 min-w-0 overflow-auto">
              <BrowserRouter>
                <WebAuthnEnrollModal />
                <Routes>
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/login/s/:slug" element={<JumppageLogin />} />
                  <Route path="/verify" element={<VerifyPage />} />
                  <Route
                    path="/prep-request"
                    element={<PrepRequestLandingPage />}
                  />
                  <Route
                    path="/substitution-request"
                    element={<SubstitutionRequestLandingPage />}
                  />
                  <Route
                    path="/retro-docs"
                    element={<RetroDocsLandingPage />}
                  />
                  <Route path="/admin/verify" element={<AdminVerifyPage />} />
                  <Route
                    path="/teacher/collection/:token"
                    element={<CollectionPhasePage />}
                  />
                  <Route
                    path="/teacher/commit/:token"
                    element={<CommitPhasePage />}
                  />
                  <Route path="/email/action" element={<EmailActionPage />} />
                  <Route
                    path="/timetable-export/open/:token"
                    element={<TimetableExportOpenPage />}
                  />
                  <Route
                    path="/auth/engineer/accept"
                    element={<EngineerAcceptPage />}
                  />
                  <Route
                    path="/dev/unicode-test"
                    element={<UnicodeTestPage />}
                  />
                  <Route path="/dev/mic-check" element={<MicCheckPage />} />

                  {/* Authenticated routes with Layout */}
                  <Route
                    element={
                      <TalkGate>
                        <AppLayout />
                      </TalkGate>
                    }
                  >
                    <Route
                      path="/dashboard"
                      element={
                        <ProtectedRoute>
                          <DashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teacher/dashboard"
                      element={
                        <ProtectedRoute minRole="teacher">
                          <TeacherDashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teacher/timetable"
                      element={
                        <ProtectedRoute minRole="teacher">
                          <TeacherTimetablePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/timetable-export"
                      element={
                        <ProtectedRoute minRole="teacher">
                          <TimetableExportPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teacher/incoming-requests"
                      element={
                        <ProtectedRoute minRole="teacher">
                          <TeacherIncomingRequestsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teacher/bookings"
                      element={
                        <ProtectedRoute minRole="teacher">
                          <TeacherBookingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teacher/requests"
                      element={
                        <ProtectedRoute minRole="teacher">
                          <TeacherRequestsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teacher/new-request"
                      element={
                        <ProtectedRoute minRole="teacher">
                          <TeacherRequestPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teacher/settings"
                      element={
                        <ProtectedRoute minRole="teacher">
                          <TeacherSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teacher/chats"
                      element={
                        <ProtectedRoute minRole="teacher">
                          <TeacherChatsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/messages"
                      element={
                        <ProtectedRoute>
                          <MessagesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/engineer/schools"
                      element={
                        <ProtectedRoute>
                          <EngineerSchoolSelectPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/teacher/request/:id"
                      element={<Navigate to="/teacher/requests" replace />}
                    />
                    <Route
                      path="/teacher/request/:id/prep"
                      element={
                        <ProtectedRoute minRole="teacher">
                          <TeacherRequestPrepPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr/reports"
                      element={
                        <ProtectedRoute minRole="hr">
                          <HrWorkRedirect>
                            <HRReportPage />
                          </HrWorkRedirect>
                        </ProtectedRoute>
                      }
                    />

                    {/* Admin routes */}
                    <Route
                      path="/admin/anfrage-fuer-lehrer"
                      element={
                        <ExactAdminRoute>
                          <AdminOnBehalfRequestPage />
                        </ExactAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/sneak-peek"
                      element={
                        <ActiveAdminRoute>
                          <SneakPeekPage />
                        </ActiveAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/stellvertretung"
                      element={
                        <ExactAdminRoute scope="substitution.direct.assign">
                          <AdminStellvertretungPage />
                        </ExactAdminRoute>
                      }
                    />
                    <Route
                      path="/admin/substitutions"
                      element={
                        <ProtectedRoute minRole="admin">
                          <SubstitutionManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/waiting-pool"
                      element={
                        <ProtectedRoute minRole="admin">
                          <WaitingPoolAdminPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/admins"
                      element={
                        <ProtectedRoute minRole="admin">
                          <AdminManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/teachers"
                      element={
                        <ProtectedRoute minRole="admin">
                          <AdminTeacherListPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/teachers/linking"
                      element={
                        <ProtectedRoute minRole="admin">
                          <TeacherLinkingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings"
                      element={
                        <ProtectedRoute minRole="admin">
                          <AdminSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/settings/calendar"
                      element={
                        <ProtectedRoute minRole="admin">
                          <ConfigPage forcedType="calendar" listOnly />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/school-params"
                      element={
                        <ProtectedRoute minRole="admin">
                          <TeacherParamsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/qualifications"
                      element={
                        <ProtectedRoute minRole="admin">
                          <QualificationMatrixPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/timetables"
                      element={
                        <ProtectedRoute minRole="admin">
                          <TimetableManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/timetable-laden"
                      element={
                        <ProtectedRoute minRole="admin">
                          <AdminTimetableLoadPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/stundenplan"
                      element={
                        <ProtectedRoute minRole="admin">
                          <EffectiveStundenplanPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Import routes */}
                    <Route
                      path="/import/teacher-codes"
                      element={
                        <ProtectedRoute minRole="admin">
                          <TeacherCodeManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/import/code-matching-report"
                      element={
                        <ProtectedRoute minRole="admin">
                          <CodeMatchingReportPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Superadmin routes */}
                    <Route
                      path="/superadmin"
                      element={<Navigate to="/superadmin/dashboard" replace />}
                    />
                    <Route
                      path="/superadmin/dashboard"
                      element={
                        <ProtectedRoute minRole="superadmin">
                          <SuperadminDashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/superadmin/roles"
                      element={<Navigate to="/superadmin/users" replace />}
                    />
                    <Route
                      path="/superadmin/users"
                      element={
                        <ProtectedRoute minRole="superadmin">
                          <SuperadminManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/superadmin/engineers"
                      element={
                        <ProtectedRoute minRole="superadmin">
                          <EngineerManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/superadmin/rules"
                      element={
                        <Navigate
                          to="/superadmin/settings/rules_of_engagement"
                          replace
                        />
                      }
                    />
                    <Route
                      path="/superadmin/files"
                      element={
                        <ProtectedRoute minRole="superadmin">
                          <FileManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/superadmin/teacher-code"
                      element={<Navigate to="/import/teacher-codes" replace />}
                    />
                    <Route
                      path="/superadmin/code-matching-report"
                      element={
                        <Navigate to="/import/code-matching-report" replace />
                      }
                    />
                    <Route
                      path="/superadmin/audit-log"
                      element={
                        <ProtectedRoute minRole="superadmin">
                          <SuperadminAuditLogPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/superadmin/bulk-import"
                      element={
                        <Navigate
                          to="/superadmin/users?tab=bulk-import"
                          replace
                        />
                      }
                    />
                    <Route
                      path="/superadmin/pdf-training"
                      element={
                        <Navigate to="/superadmin/timetable-import" replace />
                      }
                    />
                    <Route
                      path="/superadmin/timetable-import"
                      element={
                        <ProtectedRoute minRole="superadmin">
                          <TimetableImportPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/superadmin/timetable-data-quality"
                      element={
                        <ProtectedRoute minRole="superadmin">
                          <TimetableDataQualityPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/superadmin/integrations"
                      element={
                        <Navigate
                          to="/superadmin/timetable-import?tab=evento"
                          replace
                        />
                      }
                    />
                    <Route
                      path="/superadmin/director-delegations"
                      element={
                        <Navigate to="/superadmin/settings/director" replace />
                      }
                    />
                    <Route
                      path="/superadmin/email-templates"
                      element={
                        <ProtectedRoute minRole="superadmin">
                          <EmailTemplatePreviewPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/superadmin/email-outbox"
                      element={
                        <ProtectedRoute minRole="superadmin">
                          <SuperadminEmailOutboxPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/superadmin/settings/email"
                      element={
                        <Navigate to="/superadmin/email-templates" replace />
                      }
                    />

                    {/* Settings routes — teacher-Einstellungsrechte sind per Karte/Feld in jeder Rubrik */}
                    <Route
                      path="/superadmin/settings/:configType"
                      element={
                        <ProtectedRoute minRole="superadmin">
                          <ConfigPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/superadmin/settings/teacher-params"
                      element={
                        <ProtectedRoute minRole="superadmin">
                          <TeacherParamsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/superadmin/login-page"
                      element={
                        <Navigate to="/superadmin/settings/design" replace />
                      }
                    />
                    <Route
                      path="/superadmin/patterns"
                      element={
                        <ProtectedRoute minRole="superadmin">
                          <SuperadminPatternEditorPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Legacy routes → redirect to unified config */}
                    <Route
                      path="/superadmin/holidays"
                      element={
                        <Navigate to="/superadmin/settings/calendar" replace />
                      }
                    />
                    <Route
                      path="/superadmin/reminders"
                      element={
                        <Navigate to="/superadmin/settings/reminder" replace />
                      }
                    />
                    <Route
                      path="/superadmin/panic-config"
                      element={
                        <Navigate
                          to="/superadmin/settings/panic_zone"
                          replace
                        />
                      }
                    />
                    <Route
                      path="/superadmin/calendar-config"
                      element={
                        <Navigate to="/superadmin/settings/calendar" replace />
                      }
                    />
                    <Route
                      path="/superadmin/settings/vertretung"
                      element={
                        <Navigate to="/superadmin/settings/cascade" replace />
                      }
                    />
                    <Route
                      path="/superadmin/settings/smart-batch"
                      element={
                        <Navigate to="/superadmin/settings/cascade" replace />
                      }
                    />
                    <Route
                      path="/superadmin/settings/smart_batch"
                      element={
                        <Navigate to="/superadmin/settings/cascade" replace />
                      }
                    />
                    <Route
                      path="/superadmin/settings/wave"
                      element={
                        <Navigate to="/superadmin/settings/cascade" replace />
                      }
                    />
                    <Route
                      path="/superadmin/settings/panic"
                      element={
                        <Navigate
                          to="/superadmin/settings/panic_zone"
                          replace
                        />
                      }
                    />
                    <Route
                      path="/superadmin/settings/communication"
                      element={
                        <Navigate to="/superadmin/settings/response" replace />
                      }
                    />
                    <Route
                      path="/superadmin/settings/priority-request"
                      element={
                        <Navigate
                          to="/superadmin/settings/substitution_request"
                          replace
                        />
                      }
                    />
                    <Route
                      path="/superadmin/settings/holidays"
                      element={
                        <Navigate to="/superadmin/settings/calendar" replace />
                      }
                    />
                    <Route
                      path="/superadmin/settings/jumppage"
                      element={
                        <Navigate
                          to="/superadmin/settings/display_board"
                          replace
                        />
                      }
                    />

                    {/* Director routes */}
                    <Route
                      path="/director/dashboard"
                      element={
                        <ProtectedRoute minRole="director">
                          <DirectorDashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/director/users/superadmins"
                      element={
                        <ProtectedRoute minRole="director">
                          <DirectorSuperadminPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Fallakte v2 (P6.2/E1): Master-Detail ersetzt Liste + alte Detailseite */}
                    <Route
                      path="/director/stundenplan"
                      element={
                        <ProtectedRoute minRole="director">
                          <EffectiveStundenplanPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/director/decisions"
                      element={
                        <ProtectedRoute minRole="director">
                          <DirectorCasesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/director/decisions/:id"
                      element={
                        <ProtectedRoute minRole="director">
                          <DirectorCasesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/director/delegations"
                      element={
                        <ProtectedRoute minRole="director">
                          <DirectorDelegationPage />
                        </ProtectedRoute>
                      }
                    />
                    {/* Direktorkonfig v2 (D11): eigener Einstellungen-Bereich */}
                    <Route
                      path="/director/settings"
                      element={
                        <ProtectedRoute minRole="director">
                          <DirectorSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr"
                      element={
                        <ProtectedRoute minRole="hr">
                          <HRHomePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr/director-decisions"
                      element={
                        <ProtectedRoute minRole="hr">
                          <HrWorkRedirect>
                            <HRDirectorDecisionsPage />
                          </HrWorkRedirect>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr/pult"
                      element={
                        <ProtectedRoute minRole="hr">
                          <HrWorkRedirect>
                            <HRLehrerpultPage />
                          </HrWorkRedirect>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr/tabelle"
                      element={
                        <ProtectedRoute minRole="hr">
                          <HrWorkRedirect>
                            <HRAbrechnungstabellePage />
                          </HrWorkRedirect>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr/finance"
                      element={
                        <ProtectedRoute minRole="hr">
                          <HrWorkRedirect>
                            <Navigate to="/hr/pult" replace />
                          </HrWorkRedirect>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr/ipb"
                      element={
                        <ProtectedRoute minRole="hr">
                          <HrWorkRedirect>
                            <HRIpbPage />
                          </HrWorkRedirect>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/hr/settings"
                      element={
                        <ProtectedRoute minRole="hr">
                          <HrWorkRedirect>
                            <HRSettingsPage />
                          </HrWorkRedirect>
                        </ProtectedRoute>
                      }
                    />
                    {/* hr2-v2 (E1): Eigenbau-Scopes-Seite integriert in /superadmin/settings/hr —
                  Redirect fängt Bookmarks/Alt-Links ab (keine parametrisierten Links vorhanden). */}
                    <Route
                      path="/superadmin/hr-scopes"
                      element={
                        <Navigate to="/superadmin/settings/hr" replace />
                      }
                    />

                    {/* Admin routes */}
                    <Route
                      path="/admin/bookings"
                      element={
                        <Navigate to="/admin/substitutions-log" replace />
                      }
                    />
                    <Route
                      path="/admin/substitutions-log"
                      element={
                        <ProtectedRoute minRole="admin">
                          <SubstitutionsLogPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/live-status"
                      element={
                        <ProtectedRoute minRole="admin">
                          <LiveStatusPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/auto-re-request"
                      element={
                        <ProtectedRoute minRole="admin">
                          <AutoReRequestPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/cancellations"
                      element={
                        <ProtectedRoute minRole="admin">
                          <CancellationManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/admin/conflicts"
                      element={
                        <ProtectedRoute minRole="admin">
                          <ConflictManagementPage />
                        </ProtectedRoute>
                      }
                    />

                    {/* Ultraadmin routes */}
                    <Route
                      path="/ultraadmin"
                      element={<Navigate to="/ultraadmin/dashboard" replace />}
                    />
                    <Route
                      path="/ultraadmin/dashboard"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <UltraadminDashboardPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ultraadmin/superusers"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <SuperuserManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ultraadmin/statistics"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <UltraadminStatisticsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ultraadmin/schools"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <SchoolManagementPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ultraadmin/act-as"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <UltraadminActAsTeacherPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ultraadmin/partners"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <UltraadminPartnersPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/partner/schools"
                      element={
                        <ProtectedRoute minRole="partner">
                          <PartnerSchoolsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/partner/superadmins"
                      element={
                        <ProtectedRoute minRole="partner">
                          <PartnerSuperadminsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/partner/team"
                      element={
                        <ProtectedRoute minRole="partner">
                          <PartnerTeamPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/partner/billing"
                      element={
                        <ProtectedRoute minRole="partner">
                          <PartnerBillingPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ultraadmin/settings"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <UltraadminSettingsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ultraadmin/settings/billing"
                      element={
                        <Navigate to="/ultraadmin/settings#zahlung" replace />
                      }
                    />
                    <Route
                      path="/ultraadmin/prices"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <UltraadminListPricesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ultraadmin/school-terms"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <UltraadminSchoolTermsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ultraadmin/invoices"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <UltraadminInvoicesPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ultraadmin/billing-tickets"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <UltraadminBillingTicketsPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ultraadmin/overdue"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <UltraadminOverduePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/zahlung"
                      element={
                        <BillingRoute>
                          <SchoolBillingPage />
                        </BillingRoute>
                      }
                    />
                    <Route
                      path="/ultraadmin/patterns"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <UltraadminPatternPipelinePage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/ultraadmin/patterns/global"
                      element={
                        <ProtectedRoute minRole="ultraadmin">
                          <UltraadminGlobalPatternsPage />
                        </ProtectedRoute>
                      }
                    />

                    <Route
                      path="/talk"
                      element={
                        <ProtectedRoute>
                          <div />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/help"
                      element={
                        <ProtectedRoute>
                          <HelpPage />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/handbook"
                      element={<Navigate to="/help" replace />}
                    />
                  </Route>

                  <Route path="/pay/:token" element={<PublicPayPage />} />
                  <Route
                    path="/"
                    element={<Navigate to="/dashboard" replace />}
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </BrowserRouter>
            </div>
          </div>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
